import { Clock, Edit2, Trash2, GripVertical, FileAudio, Music } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RichTextDisplay } from '@/components/common/RichTextDisplay';
import { cn } from '@/lib/utils';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { MusikkFil } from '@/services/api';

interface EventRowProps {
  event: {
    id: number;
    navn: string;
    type?: 'overskrift' | 'hendelse' | 'sang' | 'musikk_placeholder';
    varighet_minutter?: number;
    beskrivelse?: string;
    musikk_lengde_minutter?: number;
    musikk_lengde_sekunder?: number;
    musikk_filer?: MusikkFil[];
  };
  onEdit: () => void;
  onDelete: () => void;
  showDescription?: boolean;
  startTime?: string | null; // For PlanEditor - beregnet starttid
  activeElementId?: number | null; // For drop indicator
  className?: string;
}

export function EventRow({ 
  event, 
  onEdit, 
  onDelete, 
  showDescription = false,
  startTime = null,
  activeElementId = null,
  className 
}: EventRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({ id: event.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const formatDurationShort = (minutter: number): string => {
    const minutes = Math.floor(minutter);
    const seconds = Math.floor((minutter % 1) * 60);
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const showDropIndicator = isOver && activeElementId !== event.id;

  return (
    <div className="relative">
      {/* Drop indicator - vises OVER innslaget */}
      {showDropIndicator && (
        <div className="absolute -top-1 left-0 right-0 h-0.5 bg-primary z-50 rounded-full shadow-lg">
          <div className="absolute left-1/2 -translate-x-1/2 -top-2 w-4 h-4 bg-primary rounded-full border-2 border-white shadow-lg" />
        </div>
      )}
      
      <div
        ref={setNodeRef}
        style={style}
        className={cn(
          'bg-card border-b border-border/50 hover:bg-accent/30 transition-all',
          isDragging ? 'opacity-0' : '',
          showDropIndicator ? 'ring-2 ring-primary/50' : '',
          className
        )}
      >
        <div className="flex items-start gap-1 sm:gap-2 p-2 sm:p-3">
          {/* Drag handle */}
          <div 
            {...attributes} 
            {...listeners}
            className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground flex items-center shrink-0"
          >
            <GripVertical className="h-4 w-4 sm:h-5 sm:w-5" />
          </div>

          {/* Planlagt starttid (kun hvis oppgitt) */}
          {startTime !== null && (
            <div className="text-xs sm:text-sm font-mono text-foreground/80 min-w-[40px] sm:min-w-[50px] shrink-0">
              {startTime || '--:--'}
            </div>
          )}

          {/* Varighet */}
          {event.varighet_minutter !== undefined && (
            <Badge 
              variant="outline" 
              className="gap-0.5 sm:gap-1 text-xs bg-primary/10 text-primary border-primary/30 font-mono shrink-0"
            >
              <Clock className="h-3 w-3" />
              <span className="hidden xs:inline">
                {formatDurationShort(event.varighet_minutter)}
              </span>
            </Badge>
          )}

          {/* Innslag navn */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              {event.type === 'musikk_placeholder' && (
                <Badge 
                  variant="outline" 
                  className="gap-0.5 sm:gap-1 text-xs bg-purple-50 text-purple-700 border-purple-200 shrink-0"
                >
                  <Music className="h-3 w-3" />
                  <span className="hidden xs:inline">Placeholder</span>
                </Badge>
              )}
              <p className="font-medium text-xs sm:text-sm text-foreground truncate">{event.navn}</p>
            </div>
            {showDescription && event.beskrivelse && (
              <div className="mt-0.5 hidden sm:block">
                <RichTextDisplay 
                  content={event.beskrivelse}
                />
              </div>
            )}
          </div>

          {/* Lydfiler indikator */}
          {event.type === 'sang' && event.musikk_filer && event.musikk_filer.length > 0 && (
            <Badge 
              variant="outline" 
              className="gap-0.5 sm:gap-1 text-xs bg-muted border-muted-foreground/30 text-muted-foreground shrink-0"
              title={`${event.musikk_filer.length} ${event.musikk_filer.length === 1 ? 'fil' : 'filer'}`}
            >
              <FileAudio className="h-3 w-3" />
              <span className="hidden xs:inline">{event.musikk_filer.length}</span>
            </Badge>
          )}

          {/* Actions */}
          <div className="flex items-center gap-0.5 sm:gap-1 shrink-0">
            <Button
              variant="ghost"
              size="icon"
              onClick={onEdit}
              className="h-6 w-6 sm:h-7 sm:w-7 text-primary hover:bg-primary/10"
            >
              <Edit2 className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              onClick={onDelete}
              className="h-6 w-6 sm:h-7 sm:w-7 text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

